import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// The pre-launch waitlist.
//
// This is the most sensitive list the API holds, and the reason is worth
// stating: every row is a real person who gave an email address before the
// product existed. None of them has an account. If the list leaks, none of
// them has anything to secure or reset.
//
// So the read is ADMIN ONLY. The write is public, because a person on the
// landing page has no account to authenticate with.
//
// The CSV path answers `text/csv`, NOT the usual `{ success, data }` envelope.
// That is deliberate — the point of it is to be saved and opened in a mail
// tool. It is the one exception in this module and the request asks for it
// explicitly rather than the server guessing from a header.
// ============================================================================

export interface WaitlistSignup {
  id: string;
  fullName: string;
  email: string;
  city: string;
  originState: string | null;
  source: string;
  utm: Record<string, string> | null;
  createdAt: string;
}

/** ADMIN ONLY. Newest first. */
export function listWaitlist(params?: {
  source?: "landing" | "business" | "organiser";
  limit?: number;
  offset?: number;
}) {
  return api.get<ApiResponse<{ signups: WaitlistSignup[]; total: number }>>(
    "/waitlist",
    params as Record<string, string | number | boolean | undefined>,
  );
}

/**
 * The same list as a file the browser downloads.
 *
 * This does not go through the shared client, because the client parses JSON
 * and this answers text. Invites are the whole reason the list exists, so the
 * export path has to be one click rather than a script somebody writes.
 */
export function waitlistCsvUrl(source?: "landing" | "business" | "organiser"): string {
  const base = import.meta.env.VITE_API_URL ?? "";
  const q = new URLSearchParams({ format: "csv", limit: "500" });
  if (source) q.set("source", source);
  return `${base}/waitlist?${q.toString()}`;
}
